import { useEffect, useRef, useState, type Dispatch, type SetStateAction, type FC } from 'react';
import { useCreateBettieMessage } from '../../../domains/bettie/ui/hooks/useCreateBettieMessage';
import type { components } from '../../../types/schema';
// import { PlusCircle } from '@peacecover/icon';
import { trackError } from '../../../utils/highlightUtils';
import { t } from 'i18next';

type ChatRoomMessage = components['schemas']['ChatRoomMessage'];

export interface BettieMessageInputProps {
  chatRoomUuid: string;
  onMessageSent: (response?: ChatRoomMessage[]) => void;
  onBeforeSend?: (content: string) => void;
  onSendFailed?: () => void;
  disabled?: boolean;
  setFreeChatCount?: Dispatch<SetStateAction<number>>;
  onBettieReplies?: (ids: string[]) => void;
}

const BettieMessageInput: FC<BettieMessageInputProps> = ({
  chatRoomUuid,
  onMessageSent,
  onBeforeSend,
  onSendFailed,
  disabled = false,
  setFreeChatCount,
  onBettieReplies
}) => {
  const [message, setMessage] = useState('');
  const createMessage = useCreateBettieMessage();
  const [isSending, setIsSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [lineCount, setLineCount] = useState(1);
  const didOptimisticallyDecrementRef = useRef(false);

  const MAX_ROWS = 3;
  const LINE_HEIGHT_PX = 18; // 라인 높이 기준값
  const MAX_COUNT = 300;

  const isMultiline = lineCount >= 2;
  const isDisabledOrSending = disabled || isSending;
  const isSendDisabled = !message.trim() || isDisabledOrSending;
  const placeholderText = `${disabled ? t('BettieChatRoom.disabledPlaceHolder') : isSending ? t('BettieChatRoom.isSendingPlaceHolder') : t('BettieChatRoom.activePlaceHolder')}`;

  const focusInput = () => textareaRef.current?.focus();

  const adjustTextareaSize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = '0px';
    const maxHeight = LINE_HEIGHT_PX * MAX_ROWS;
    const nextHeight = Math.min(el.scrollHeight, maxHeight);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
    const estimatedLines = Math.max(1, Math.round(el.scrollHeight / LINE_HEIGHT_PX));
    setLineCount(estimatedLines);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: adjustTextareaSize is dependent on message
  useEffect(() => {
    requestAnimationFrame(adjustTextareaSize);
  }, [message]);

  const sendMessage = async (content: string) => {
    if (!content.trim() || isSending || disabled) return;

    // 낙관적 업데이트를 위한 사전 콜백
    try {
      onBeforeSend?.(content.trim());
    } catch (_) {
      trackError(
        new Error('Failed to create optimistic message'),
        'Failed to create optimistic message',
        {
          function: 'BettieMessageInput',
          context: 'failed_to_create_optimistic_message',
          currentPath: `${window.location.pathname}${window.location.search}`,
          content: content.trim()
        }
      );
    }

    if (setFreeChatCount) {
      setFreeChatCount((prev) => {
        if (prev > 0) {
          didOptimisticallyDecrementRef.current = true;
          return prev - 1;
        }
        return prev;
      });
    }

    setIsSending(true);
    setMessage('');
    // keep keyboard open by immediately refocusing the input
    requestAnimationFrame(focusInput);
    requestAnimationFrame(adjustTextareaSize);

    try {
      const requestBody = {
        message: {
          chat_room_id: chatRoomUuid,
          content: content.trim(),
          message_type: 'text',
          sent_at: new Date().toISOString()
        }
      } as const;

      const data = await createMessage.mutateAsync(requestBody);
      // ensure focus stays on the input after send completes
      requestAnimationFrame(focusInput);

      if (!data) {
        throw new Error('Empty response when creating message');
      }

      if (data.userMessage) {
        // 유저의 메시지가 response로 온 경우 refetch 처리
        onMessageSent();
      }

      // Bettie 응답 메시지 ID들을 부모에 전달하여 순차 노출 트리거
      try {
        const replyIds =
          (data.bettieMessages || [])
            .map((m) => m?.chatRoomMessageId)
            .filter((v): v is string => Boolean(v)) ?? [];
        if (replyIds.length > 0) {
          onBettieReplies?.(replyIds);
        }
      } catch (_) {
        // no-op
      }

      if (data.meta.bettieChatsCount !== undefined && data.meta.bettieChatsCount !== null) {
        // Avoid increasing the count with a potentially stale server value after an optimistic decrement
        setFreeChatCount?.((prev) => Math.min(prev, data.meta.bettieChatsCount as number));
      }
      didOptimisticallyDecrementRef.current = false;
    } catch (error) {
      onSendFailed?.();
      if (didOptimisticallyDecrementRef.current && setFreeChatCount) {
        setFreeChatCount((prev) => prev + 1);
      }
      didOptimisticallyDecrementRef.current = false;
      trackError(new Error(`Failed to send message: ${error}`), 'Failed to send message', {
        function: 'BettieMessageInput',
        context: 'failed_to_send_message',
        currentPath: `${window.location.pathname}${window.location.search}`,
        content: {
          content: content.trim(),
          chat_room_uuid: chatRoomUuid
        }
      });
    } finally {
      setIsSending(false);
      // do not blur the input when sending finishes
      requestAnimationFrame(focusInput);
    }
  };

  const handleSendMessage = () => {
    if (message.trim()) {
      sendMessage(message);
    }
  };

  return (
    <div
      className={`flex px-[10px] pt-[8px] pb-[10px] gap-[4px] w-full ${isMultiline ? 'items-end' : 'items-center'}`}
    >
      {/* <div className={`w-[36px] h-[36px] ${isMultiline ? 'mb-[8px]' : ''}`}>
        <PlusCircle size={36} className="text-on-disable" />
      </div> */}
      <div
        className={`flex w-full gap-[6px] ${
          isMultiline
            ? 'rounded-[20px] px-[6px] py-[8px] items-end'
            : 'rounded-[30px] px-[8px] py-[6px]'
        } ${isDisabledOrSending ? 'bg-[#F7F7F7] dark:bg-disabled-dark' : 'bg-secondary-light dark:bg-secondary-dark'}`}
      >
        <div className="flex flex-col justify-center w-full">
          <textarea
            id="bettie-message-input"
            ref={textareaRef}
            value={message}
            rows={1}
            maxLength={MAX_COUNT}
            disabled={disabled}
            placeholder={placeholderText}
            className="bg-transparent ml-[8px] font-medium text-[15px] leading-[18px] resize-none outline-none placeholder:text-placeholder-light dark:placeholder:text-placeholder-dark [&::-webkit-scrollbar]:hidden"
            onChange={(e) => {
              if (isSending) {
                return;
              }
              setMessage(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (message.trim()) {
                  sendMessage(message);
                }
              }
            }}
          />

          {/* 글자수 카운터 */}
          {message.length >= 200 && (
            <p className="text-[11px] text-on-warning mt-[8px] text-right">
              {message.length}/{MAX_COUNT}
            </p>
          )}
        </div>

        <button
          type="button"
          disabled={isSendDisabled}
          onClick={handleSendMessage}
          onMouseDown={(e) => {
            // prevent the button from stealing focus, which would close the keyboard on mobile
            e.preventDefault();
          }}
          tabIndex={-1}
          className="p-[5.5px] shrink-0 inline-flex items-center justify-center"
        >
          <img
            src={`/assets/icons/${isSendDisabled ? 'disabled-up' : 'up'}.svg`}
            alt="send"
            className="w-[25px] h-[25px] flex-none"
          />
        </button>
      </div>
    </div>
  );
};

export default BettieMessageInput;
